import React, { useEffect, useState, useCallback } from 'react';
import { getCommentsByPostId, deleteComment, updateComment, createComment } from '../../api/boardApi';
import CommentForm from './CommentForm';
import UserDetailModal from '../User/UserDetailModal'; // ✅ 사용자 정보 모달 추가
import './Comment.css';

const CommentList = ({ postId, currentUser, token, codeId }) => {
  const [comments, setComments] = useState([]);
  const [editCommentId, setEditCommentId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [replyToId, setReplyToId] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [photoErrors, setPhotoErrors] = useState({}); // 사진 로드 실패 추적

  // ✅ 사용자 상세 정보 모달 상태
  const [selectedUserid, setSelectedUserid] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openUserModal = (userid) => {
    setSelectedUserid(userid);
    setIsModalOpen(true);
  };

  // 날짜 포맷
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours < 12 ? '오전' : '오후';
    const hour12 = hours % 12 || 12;
    return `${year}-${month}-${day} ${ampm} ${hour12}:${minutes}`;
  };

  // 아바타 색상 결정 (더 차분한 톤으로 변경)
  const getAvatarColor = (name) => {
    const colors = ['#e9ecef', '#f1f3f5', '#dee2e6', '#ced4da'];
    const index = (name ? name.charCodeAt(0) : 0) % colors.length;
    return colors[index];
  };

  // 댓글 불러오기
  const loadComments = useCallback(async () => {
    try {
      const data = await getCommentsByPostId(postId);
      setComments(data);
    } catch (err) {
      console.error('댓글 불러오기 실패:', err);
    }
  }, [postId]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  // 댓글 삭제
  const handleDelete = async (id) => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;
    try {
      await deleteComment(id, token);
      loadComments();
    } catch (err) {
      console.error('댓글 삭제 실패:', err);
    }
  };

  // 댓글 수정
  const handleEditSubmit = async (id) => {
    if (!editContent.trim()) return;
    try {
      await updateComment(id, { content: editContent }, token);
      setEditCommentId(null);
      setEditContent('');
      loadComments();
    } catch (err) {
      console.error('댓글 수정 실패:', err);
    }
  };

  // 답글 작성
  const handleReplySubmit = async (parentId, depth) => {
    if (!replyContent.trim()) return;
    try {
      await createComment(
        {
          post_id: postId,
          code_id: codeId,
          content: replyContent,
          parent_id: parentId,
          depth: depth + 1,
        },
        token
      );
      setReplyToId(null);
      setReplyContent('');
      loadComments();
    } catch (err) {
      console.error('답글 작성 실패:', err);
    }
  };

  // ✅ 댓글을 트리 구조로 변환
  const buildCommentTree = (comments) => {
    const map = {};
    const roots = [];

    comments.forEach((c) => {
      if (!c.is_deleted) {
        map[c.id] = { ...c, children: [] };
      }
    });

    Object.values(map).forEach((c) => {
      if (c.parent_id) {
        if (map[c.parent_id]) {
          map[c.parent_id].children.push(c);
        }
      } else {
        roots.push(c);
      }
    });

    return roots;
  };

  // ✅ 재귀적으로 댓글 렌더링
  const renderComments = (commentList) =>
    commentList.map((comment) => {
      // 0: 시도 안함, 1: 회사 폴더 실패, 2: 전체 실패
      const errorLevel = photoErrors[comment.id] || 0;

      // 1단계: /user_photo/회사코드/아이디.png
      // 2단계: /user_photo/아이디.png
      const photoPath = errorLevel === 1
        ? `/user_photo/${comment.writer_id}.png`
        : `/user_photo/${currentUser?.M_ComCode}/${comment.writer_id}.png`;

      const hasFailedAll = errorLevel >= 2;

      return (
        <div key={comment.id} className="comment-item">
          <div className="comment-main-wrapper">
            {/* 아바타 */}
            <div
              className="comment-avatar"
              style={{ backgroundColor: getAvatarColor(comment.writer_name), cursor: 'pointer' }}
              onClick={() => openUserModal(comment.writer_id)}
              title={`${comment.writer_name}님의 정보 보기`}
            >
              {!hasFailedAll ? (
                <img
                  src={photoPath}
                  alt=""
                  loading="lazy" /* 지연 로딩: 화면에 보일 때만 로드 */
                  decoding="async" /* 비동기 디코딩: 렌더링 성능 향상 */
                  onError={() => setPhotoErrors(prev => ({
                    ...prev,
                    [comment.id]: (prev[comment.id] || 0) + 1
                  }))}
                />
              ) : (
                comment.writer_name ? comment.writer_name.charAt(0) : '?'
              )}
            </div>

            <div className="comment-content-container">
              {/* 헤더 */}
              <div className="comment-header">
                <span className="comment-writer-name">{comment.writer_name}</span>
                <span className="comment-date">{formatDateTime(comment.created_at)}</span>
              </div>

              {/* 본문/수정창 */}
              {editCommentId === comment.id ? (
                <div className="inline-reply-form">
                  <textarea
                    className="comment-form-textarea"
                    rows="3"
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                  />
                  <div className="comment-submit-row">
                    <button className="comment-submit-btn cancel" onClick={() => setEditCommentId(null)}>취소</button>
                    <button className="comment-submit-btn" onClick={() => handleEditSubmit(comment.id)}>수정 완료</button>
                  </div>
                </div>
              ) : (
                <div className="comment-body-text">{comment.content}</div>
              )}

              {/* 액션 버튼 */}
              <div className="comment-actions">
                {currentUser && comment.depth < 2 && replyToId !== comment.id && (
                  <button className="comment-action-btn" onClick={() => setReplyToId(comment.id)}>
                    <i className="fas fa-reply"></i> 답글
                  </button>
                )}
                {(currentUser?.userid === comment.writer_id || currentUser?.role === 'admin') && editCommentId !== comment.id && (
                  <>
                    <button
                      className="comment-action-btn"
                      onClick={() => {
                        setEditCommentId(comment.id);
                        setEditContent(comment.content);
                      }}
                    >
                      <i className="fas fa-edit"></i> 편집
                    </button>
                    <button className="comment-action-btn delete" onClick={() => handleDelete(comment.id)}>
                      <i className="fas fa-trash-alt"></i> 삭제
                    </button>
                  </>
                )}
              </div>

              {/* 답글 입력창 */}
              {currentUser && replyToId === comment.id && (
                <div className="inline-reply-form">
                  <textarea
                    className="comment-form-textarea"
                    rows="3"
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="답글을 입력하세요..."
                  />
                  <div className="comment-submit-row">
                    <button className="comment-submit-btn cancel" onClick={() => setReplyToId(null)}>취소</button>
                    <button className="comment-submit-btn" onClick={() => handleReplySubmit(comment.id, comment.depth)}>답글 등록</button>
                  </div>
                </div>
              )}

              {/* 자식 댓글 재귀 렌더링 */}
              {comment.children.length > 0 && (
                <div className="nested-comments">
                  {renderComments(comment.children)}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    });

  return (
    <div className="comment-section-container">
      <h3 className="comment-count-title">댓글 {comments.length > 0 ? `(${comments.length})` : ''}</h3>
      {currentUser && (
        <CommentForm
          postId={postId}
          codeId={codeId}
          onSuccess={loadComments}
          token={token}
        />
      )}
      <div className="comment-list-wrapper">
        {comments.length > 0 ? (
          renderComments(buildCommentTree(comments))
        ) : (
          <div className="text-center py-4 text-muted small">첫 댓글을 남겨보세요.</div>
        )}
      </div>
      {/* 사용자 상세 정보 모달 */}
      <UserDetailModal 
        userid={selectedUserid}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        token={token}
      />
    </div>
  );
};

export default CommentList;